import * as React from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { CheckboxOption } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { TestQueryPanel } from "@/components/admin/TestQueryPanel"
import { ScheduleFields } from "@/components/automations/ScheduleFields"
import { MessageEditor, messageBlocksSave } from "@/components/automations/MessageEditor"
import {
  LanguageVersions,
  defaultsPayload,
  draftsFromDefaults,
  languagesBlockSave,
} from "@/components/automations/LanguageVersions"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { PageProps } from "@/types/inertia"
import {
  type AutomationDetail,
  type AutomationSchedule,
  type LanguageOptions,
  type ScopeOption,
} from "@/types/automation"

const REQUIRED_COLUMNS = "id, mobile_phone, lu, filter_dc, opt_out"

export function AutomationForm({
  automation,
  scopeOptions,
  submitLabel,
  languageOptions,
}: {
  automation: AutomationDetail
  scopeOptions: ScopeOption[]
  submitLabel: string
  /** Deferred: undefined until the roster has been asked. */
  languageOptions?: LanguageOptions
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const { data, setData, transform, post, patch, processing, isDirty } = useForm({
    translations: draftsFromDefaults(automation.translations),
    name: automation.name ?? "",
    description: automation.description ?? "",
    default_message: automation.default_message ?? "",
    roster_query: automation.roster_query ?? "",
    default_send_time: automation.default_send_time ?? "09:00",
    default_frequency: automation.default_frequency,
    default_weekday: automation.default_weekday,
    default_monthly_mode: automation.default_monthly_mode,
    default_week_ordinal: automation.default_week_ordinal,
    default_day_of_month: automation.default_day_of_month,
    is_active: automation.is_active,
    allows_repeat_setups: automation.allows_repeat_setups,
    iupat_only: automation.iupat_only,
  })

  // The schedule control speaks in unprefixed names; an automation stores the
  // default an organization starts from, so its columns carry default_.
  const schedule: AutomationSchedule = {
    frequency: data.default_frequency,
    weekday: data.default_weekday,
    monthly_mode: data.default_monthly_mode,
    week_ordinal: data.default_week_ordinal,
    day_of_month: data.default_day_of_month,
  }

  const updateSchedule = (patch: Partial<AutomationSchedule>) => {
    if (patch.frequency !== undefined) setData("default_frequency", patch.frequency)
    if (patch.weekday !== undefined) setData("default_weekday", patch.weekday)
    if (patch.monthly_mode !== undefined)
      setData("default_monthly_mode", patch.monthly_mode)
    if (patch.week_ordinal !== undefined)
      setData("default_week_ordinal", patch.week_ordinal)
    if (patch.day_of_month !== undefined)
      setData("default_day_of_month", patch.day_of_month)
  }

  // Variables come from the query. Until one has been tested in this session,
  // fall back to whatever the last save discovered.
  const [variables, setVariables] = React.useState(automation.variables)

  // A half-written query must survive an iOS resume.
  useBlockAutoReload(isDirty)

  // The server refuses the same things; blocking here saves the round trip.
  const blocked =
    messageBlocksSave(data.default_message, variables) ||
    languagesBlockSave(data.translations, variables)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (blocked) return

    // Nested explicitly: ParamsWrapper keeps only real columns under
    // `automation`, and translations is not one.
    transform((form) => ({
      automation: { ...form, translations: defaultsPayload(form.translations) },
    }))

    if (automation.id) {
      patch(`/admin/automations/${automation.id}`)
    } else {
      post("/admin/automations")
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 space-y-8">
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name">Name</label>
          <Input
            id="name"
            value={data.name}
            onChange={(event) => setData("name", event.target.value)}
            aria-invalid={Boolean(errors.name)}
            autoComplete="off"
          />
          <FieldNote error={errors.name}>
            What staff see in the automation catalog.
          </FieldNote>
        </div>

        <div className="space-y-2">
          <label htmlFor="description">Description</label>
          <Textarea
            id="description"
            rows={2}
            value={data.description}
            onChange={(event) => setData("description", event.target.value)}
            aria-invalid={Boolean(errors.description)}
          />
          <FieldNote error={errors.description}>
            A sentence explaining who it reaches and when.
          </FieldNote>
        </div>

        <div className="space-y-2">
          <label htmlFor="roster_query">Roster query</label>
          <Textarea
            id="roster_query"
            mono
            rows={10}
            value={data.roster_query}
            onChange={(event) => setData("roster_query", event.target.value)}
            aria-invalid={Boolean(errors.roster_query)}
            placeholder={"SELECT " + REQUIRED_COLUMNS + ", first_name\nFROM …"}
          />
          <FieldNote error={errors.roster_query}>
            A read-only query describing what makes a member eligible. It must
            return {REQUIRED_COLUMNS}; every other column it returns becomes a
            message variable. Say nothing about District Councils or Locals — the
            app narrows the results to whatever each organization targets.
          </FieldNote>
        </div>

        <TestQueryPanel
          rosterQuery={data.roster_query}
          scopeOptions={scopeOptions}
          onVariables={setVariables}
        />

        <MessageEditor
          id="default_message"
          label="English Message"
          value={data.default_message}
          variables={variables}
          error={errors.default_message}
          onChange={(value) => setData("default_message", value)}
          hint="The wording every organization starts from. They can change it."
          unknownVariableNote="is not returned by this query, so it will come out blank. Saving is still allowed."
        />

        {/* Default versions in other languages. Each organization can use one,
            change it, or leave the language out. */}
        <LanguageVersions
          drafts={data.translations}
          onChange={(drafts) => setData("translations", drafts)}
          variables={variables}
          languageOptions={languageOptions}
          max={automation.max_translations}
          errors={errors}
          hint="The default in this language. Organizations can change it or leave it out."
        />

        <fieldset className="space-y-3">
          <legend>Default send schedule</legend>
          <p className="text-xs text-ink-muted">
            What organizations start from before they change it. Each District
            Council can pick a different repeat and a different time on its own
            setup page.
          </p>

          <ScheduleFields
            value={schedule}
            onChange={updateSchedule}
            idPrefix="default"
            errors={{
              frequency: errors.default_frequency,
              weekday: errors.default_weekday,
              monthly_mode: errors.default_monthly_mode,
              week_ordinal: errors.default_week_ordinal,
              day_of_month: errors.default_day_of_month,
            }}
          />

          <div className="space-y-2">
            <label htmlFor="default_send_time">At</label>
            <Input
              id="default_send_time"
              type="time"
              className="max-w-40"
              value={data.default_send_time}
              onChange={(event) => setData("default_send_time", event.target.value)}
              aria-invalid={Boolean(errors.default_send_time)}
            />
            <FieldNote error={errors.default_send_time}>
              The time of day organizations start with. The zone is each
              organization's own choice, made on its setup page.
            </FieldNote>
          </div>
        </fieldset>

        <CheckboxOption
          checked={data.is_active}
          onChange={(event) => setData("is_active", event.target.checked)}
          label="Active — show this automation in the catalog. It needs a working query first."
        />

        {/* A meeting reminder is not one automation per District Council; it is
            one per Local, each with its own time, its own place and its own
            wording. Everything without this stays capped at one setup per
            organization, which is what stops a member getting two birthday
            texts. */}
        <div className="space-y-2">
          <CheckboxOption
            checked={data.allows_repeat_setups}
            onChange={(event) =>
              setData("allows_repeat_setups", event.target.checked)
            }
            label="Organizations may set this up more than once — one copy per Local, each with its own Locals, schedule and wording. Leave it clear and an organization gets exactly one setup."
          />
          {errors.allows_repeat_setups && (
            <p className="text-xs text-danger-display">
              {errors.allows_repeat_setups}
            </p>
          )}
        </div>

        {/* Some automations only make sense for the International. Flagged,
            this one is offered to IUPAT alone and never appears for a District
            Council or a Local. */}
        <div className="space-y-2">
          <CheckboxOption
            checked={data.iupat_only}
            onChange={(event) => setData("iupat_only", event.target.checked)}
            label="International only — offer this to IUPAT alone. District Councils and Locals never see it."
          />
          {errors.iupat_only && (
            <p className="text-xs text-danger-display">{errors.iupat_only}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={processing || blocked}>
          {processing ? "Saving…" : submitLabel}
        </Button>
        <Button variant="ghost" asChild>
          <Link href="/admin/automations">Cancel</Link>
        </Button>
      </div>
    </form>
  )
}

function FieldNote({
  error,
  children,
}: {
  error?: string
  children: React.ReactNode
}) {
  if (error) return <p className="text-xs text-danger-display">{error}</p>

  return <p className="text-xs text-ink-muted">{children}</p>
}
